import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Search, Trash2, AlertTriangle } from "lucide-react";

const KpiCard = ({ label, value, sub, testId, toneClass }) => (
  <div className="bg-card border border-border rounded-lg p-5 shadow-sm" data-testid={testId}>
    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 ${toneClass || ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
  </div>
);

const ActionBadge = ({ action }) =>
  action === "remove" ? (
    <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/15">Remove</Badge>
  ) : (
    <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15">Review</Badge>
  );

const BREAKDOWN_COLS = [
  ["customer_damaged", "Customer dmg"],
  ["warehouse_damaged", "Warehouse dmg"],
  ["carrier_damaged", "Carrier dmg"],
  ["distributor_damaged", "Distributor dmg"],
  ["defective", "Defective"],
  ["expired", "Expired"],
];

export const InventoryRemoval = () => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");

  const load = () => api.get("/tools/inventory-removal").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/tools/inventory-removal/sync");
      toast.info("Refreshing FBA inventory from Amazon — takes 1–2 minutes");
      setTimeout(() => { load(); setSyncing(false); }, 90000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;

  const query = q.trim().toLowerCase();
  const match = (r) =>
    !query ||
    (r.name || "").toLowerCase().includes(query) ||
    (r.sku || "").toLowerCase().includes(query) ||
    (r.asin || "").toLowerCase().includes(query);
  const unsellable = data.unsellable.filter(match);
  const nonMoving = data.non_moving.filter(match);

  return (
    <div className="space-y-6" data-testid="inventory-removal">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Inventory Removal</h2>
          <p className="text-xs text-muted-foreground">
            Unsellable FBA stock + removal recommendations for non-moving inventory (60+ days)
            {data.last_updated && ` · inventory as of ${new Date(data.last_updated).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`}
          </p>
        </div>
        <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing || data.refreshing} data-testid="removal-sync-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${(syncing || data.refreshing) ? "animate-spin" : ""}`} aria-hidden="true" />
          {syncing || data.refreshing ? "Refreshing…" : "Refresh from Amazon"}
        </Button>
      </div>

      {data.refreshing && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-2 text-xs text-muted-foreground" data-testid="removal-refreshing-hint">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" aria-hidden="true" />
          Pulling the damaged/unsellable breakdown from Amazon for the first time — check back in 1–2 minutes.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Unsellable units" value={data.kpis.unsellable_units.toLocaleString("en-IN")} sub={`across ${data.kpis.unsellable_skus} SKUs`} testId="kpi-unsellable-units" toneClass="text-red-600 dark:text-red-400" />
        <KpiCard label="Remove — SKUs" value={data.kpis.remove_skus.toLocaleString("en-IN")} sub="no sales in 60+ days" testId="kpi-remove-skus" toneClass="text-red-600 dark:text-red-400" />
        <KpiCard label="Remove — units" value={data.kpis.remove_units.toLocaleString("en-IN")} sub="stock locked in non-movers" testId="kpi-remove-units" />
        <KpiCard label="Review — SKUs" value={data.kpis.review_skus.toLocaleString("en-IN")} sub=">180 days of stock cover" testId="kpi-review-skus" toneClass="text-amber-600 dark:text-amber-400" />
      </div>

      <div className="relative w-full max-w-xs">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, SKU or ASIN…" className="pl-9 h-9 rounded-full" data-testid="removal-search-input" />
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto">
        <div className="flex items-center gap-2 px-4 pt-4">
          <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
          <h3 className="text-sm font-semibold">Non-sellable inventory (damaged / defective / expired)</h3>
        </div>
        <p className="text-xs text-muted-foreground px-4 pb-2 pt-1">Create a removal order for these in Seller Central — they only accrue storage fees.</p>
        <table className="w-full text-sm" data-testid="unsellable-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 font-semibold">Product</th>
              <th className="text-right px-3 py-2.5 font-semibold">Unsellable</th>
              {BREAKDOWN_COLS.map(([k, label]) => (
                <th key={k} className="text-right px-3 py-2.5 font-semibold">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {unsellable.slice(0, 100).map((r) => (
              <tr key={r.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`unsellable-row-${r.sku}`}>
                <td className="pl-3 py-2 w-14"><SkuThumb sku={r.sku} alt={r.name} /></td>
                <td className="px-3 py-2">
                  <p className="font-medium max-w-[20rem] truncate" title={r.name}>{r.name || r.sku}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">{r.sku}{r.asin && ` · ${r.asin}`}</p>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-bold text-red-600 dark:text-red-400">{r.unfulfillable}</td>
                {BREAKDOWN_COLS.map(([k]) => (
                  <td key={k} className={`px-3 py-2 text-right tabular-nums ${r[k] ? "" : "text-muted-foreground/50"}`}>{r[k] || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {unsellable.length === 0 && (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="unsellable-empty">
            {query ? "No unsellable products match your search." : "No unsellable inventory right now — nothing to remove. 🎉"}
          </p>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto">
        <div className="flex items-center gap-2 px-4 pt-4">
          <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
          <h3 className="text-sm font-semibold">Removal recommendations — non-moving stock</h3>
        </div>
        <p className="text-xs text-muted-foreground px-4 pb-2 pt-1">
          <span className="text-red-600 dark:text-red-400 font-semibold">Remove</span>: zero sales in the last 60 days ·{" "}
          <span className="text-amber-600 dark:text-amber-400 font-semibold">Review</span>: over 180 days of stock at the current sales rate
        </p>
        <table className="w-full text-sm" data-testid="non-moving-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 font-semibold">Product</th>
              <th className="text-left px-3 py-2.5 font-semibold">Action</th>
              <th className="text-right px-3 py-2.5 font-semibold">FBA stock</th>
              <th className="text-right px-3 py-2.5 font-semibold">Sold 60d</th>
              <th className="text-right px-3 py-2.5 font-semibold">Sold 90d</th>
              <th className="text-right px-3 py-2.5 font-semibold">Last sale</th>
              <th className="text-left px-3 py-2.5 font-semibold">Why</th>
            </tr>
          </thead>
          <tbody>
            {nonMoving.slice(0, 200).map((r) => (
              <tr key={r.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`non-moving-row-${r.sku}`}>
                <td className="pl-3 py-2 w-14"><SkuThumb sku={r.sku} alt={r.name} /></td>
                <td className="px-3 py-2">
                  <p className="font-medium max-w-[20rem] truncate" title={r.name}>{r.name || r.sku}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">{r.sku}{r.asin && ` · ${r.asin}`}</p>
                </td>
                <td className="px-3 py-2"><ActionBadge action={r.action} /></td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.fulfillable.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.units_60d}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.units_90d}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs">
                  {r.last_sale || "—"}
                  {r.days_since_sale != null && <span className="block text-[10px] text-muted-foreground">{r.days_since_sale}d ago</span>}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground max-w-[14rem]">{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {nonMoving.length === 0 && (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="non-moving-empty">
            {query ? "No non-moving products match your search." : "No non-moving inventory — everything with stock has recent sales."}
          </p>
        )}
        {nonMoving.length > 200 && (
          <p className="text-[11px] text-muted-foreground text-center py-2">Showing top 200 of {nonMoving.length} products</p>
        )}
      </div>
    </div>
  );
};
